import { v4 as uuidv4 } from 'uuid';
import { OmniboxConfigurationService } from '../OmniboxConfigurationService';

import { TelemetryService, TelemetryEvents, SearchEventSource, SearchEPTEvent, SearchFiltersUpdatedEvent, SearchImpressionEvent, SearchInitiatedEvent, SearchNoResultEvent, SearchResultClickedEvent, SearchTermUpdatedEvent } from '../services/TelemetryService';

interface ISearchTelemetry {
    clearSearchFlowId: () => void;
    startSearchFlow: (eventSource: SearchEventSource) => void;
    isSearchFlowStarted: () => boolean;
    sendSearchTermUpdatedEvent: (eventSource: SearchEventSource, searchTerm: string) => void;
    sendSearchEPTEvent: (sessionType: string,
        searchTerm: string,
        durationFromStartToWhenInitialUIRendered: number,
        durationFromWhenInitialUIRenderedToWhenSendingRequestsToServerSide: number,
        durationFromWhenSendingRequestsToServerSideToWhenGotResponseFromServerSide: number,
        durationFromWhenGotResponseFromServerSideToRenderFullData: number,
        totalDuration: number) => void;
    endSearchFlowWithNoResultEvent: (eventSource: SearchEventSource, searchTerm: string) => void;
    sendSearchFiltersUpdatedEvent: (event: SearchFiltersUpdatedEvent) => void;
    sendSearchResultClickedEvent: (event: Omit<SearchResultClickedEvent, 'searchFlowId'>, skipClearSearchFlowId?: boolean) => void;
    sendSearchImpressionEvent: (searchTerm: string, impressionType: string) => void;
    ensureInitialization: (isOnlineEnvironemnt: boolean) => void;
}

class SearchTelemetry implements ISearchTelemetry {
    private isOnlineEnvironemnt = false;
    private searchFlowId: string = '';

    public ensureInitialization = (isOnlineEnvironemnt: boolean) => {
        this.isOnlineEnvironemnt = isOnlineEnvironemnt;
    };

    public startSearchFlow = (eventSource: SearchEventSource) => {
        this.searchFlowId = uuidv4();
        const telemetryEvent: SearchInitiatedEvent = {
            type: TelemetryEvents.SearchInitiated,
            eventSource,
            host_component: OmniboxConfigurationService.instance.configuration.hostComponent,
            searchFlowId: this.searchFlowId
        }

        TelemetryService.instance.sendSearchInitiatedEvent(telemetryEvent);
    };

    public clearSearchFlowId = () => {
        this.searchFlowId = '';
    };

    public isSearchFlowStarted = () => {
        return this.searchFlowId !== '';
    };

    public sendSearchTermUpdatedEvent = (eventSource: SearchEventSource, searchTerm: string) => {
        if (!this.isOnlineEnvironemnt) {
            searchTerm = null;
        }

        const telemetryEvent: SearchTermUpdatedEvent = {
            type: TelemetryEvents.SearchTermUpdated,
            eventSource,
            searchTerm,
            host_component: OmniboxConfigurationService.instance.configuration.hostComponent,
            searchFlowId: this.searchFlowId
        }
        TelemetryService.instance.sendSearchTermUpdatedEvent(telemetryEvent);
    };

    public sendSearchEPTEvent = (eptType: string,
        searchTerm: string,
        durationFromStartToWhenInitialUIRendered: number,
        durationFromWhenInitialUIRenderedToWhenSendingRequestsToServerSide: number,
        durationFromWhenSendingRequestsToServerSideToWhenGotResponseFromServerSide: number,
        durationFromWhenGotResponseFromServerSideToRenderFullData: number,
        totalDuration: number
    ) => {
        if (!this.isOnlineEnvironemnt) {
            searchTerm = null;
        }

        const telemetryEvent: SearchEPTEvent = {
            type: TelemetryEvents.SearchEPT,
            ept_type: eptType,
            search_term: searchTerm,
            search_flow_id: this.searchFlowId,
            duration_from_start_to_phase_a: durationFromStartToWhenInitialUIRendered,
            duration_from_phase_a_to_phase_b: durationFromWhenInitialUIRenderedToWhenSendingRequestsToServerSide,
            duration_from_phase_b_to_phase_c: durationFromWhenSendingRequestsToServerSideToWhenGotResponseFromServerSide,
            duration_from_phase_c_to_end: durationFromWhenGotResponseFromServerSideToRenderFullData,
            total_duration: totalDuration,
            host_component: OmniboxConfigurationService.instance.configuration.hostComponent
        }

        TelemetryService.instance.sendSearchEPTEvent(telemetryEvent);
    };

    public endSearchFlowWithNoResultEvent = (eventSource: SearchEventSource, searchTerm: string) => {
        if (!this.isOnlineEnvironemnt) {
            searchTerm = null;
        }

        if (this.searchFlowId === '') { return; }
        const telemetryEvent: SearchNoResultEvent = {
            type: TelemetryEvents.SearchNoResult,
            eventSource,
            searchTerm,
            host_component: OmniboxConfigurationService.instance.configuration.hostComponent,
            searchFlowId: this.searchFlowId
        }
        TelemetryService.instance.sendSearchNoResultEvent(telemetryEvent);
        this.clearSearchFlowId();
    };

    public sendSearchImpressionEvent = (searchTerm: string, impressionType: string) => {
        const telemetryEvent: SearchImpressionEvent = {
            type: TelemetryEvents.SearchImpression,
            search_flow_id: this.searchFlowId,
            searchTerm,
            host_component: OmniboxConfigurationService.instance.configuration.hostComponent,
            impression_type: impressionType
        }
        TelemetryService.instance.sendSearchImpressionEvent(telemetryEvent);
    };

    public sendSearchResultClickedEvent = (event: Omit<SearchResultClickedEvent, 'searchFlowId'>, skipClearSearchFlowId?: boolean) => {
        if (!this.isOnlineEnvironemnt) {
            event.searchTerm = null;
        }
        const telemetryEvent: SearchResultClickedEvent = {
            ...event as SearchResultClickedEvent,
            searchFlowId: this.searchFlowId
        }

        TelemetryService.instance.sendSearchResultClickedEvent(telemetryEvent);
        if (!(event.seeAll || skipClearSearchFlowId)) {
            this.clearSearchFlowId();
        }
    };

    public sendSearchFiltersUpdatedEvent = (event: SearchFiltersUpdatedEvent) => {
        if (!this.isOnlineEnvironemnt) {
            event.search_term = null;
        }

        const telemetryEvent: SearchFiltersUpdatedEvent = {
            ...event as SearchFiltersUpdatedEvent/*, search_flow_id: this.searchFlowId*/
        }
        TelemetryService.instance.sendSearchFiltersUpdatedEvent(telemetryEvent);
    };
}

export const searchTelemetry: ISearchTelemetry = new SearchTelemetry();

